import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getAdminPlanning, getTechniciens, getAbsences, getJoursFeries } from '../services/api';
import { useSettings } from '../App';
import {
  format, startOfWeek, endOfWeek, addWeeks, subWeeks,
  startOfMonth, endOfMonth, addMonths, subMonths,
  addDays, subDays,
  eachDayOfInterval, isToday,
} from 'date-fns';
import { fr } from 'date-fns/locale';

// ─── Secteur ─────────────────────────────────────────────────────────────────
function getSecteur(cp) {
  if (!cp) return null;
  const s = String(cp).trim();
  const d2 = s.slice(0, 2);
  const d1 = s.slice(0, 1);
  const secA2 = ['75','92','91','77','94','69','42','38','07','43','03','63','01','26'];
  const secB2 = ['78','93','95','13','06','83','74','73','30','84'];
  if (secA2.includes(d2)) return 'A';
  if (secB2.includes(d2)) return 'B';
  if (d1 === '7') return 'A';
  if (d1 === '3') return 'A';
  if (d1 === '1') return 'A';
  if (d1 === '6') return 'B';
  return null;
}

const TECH_COLORS = [
  '#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#06b6d4',
  '#f97316', '#ec4899', '#14b8a6', '#84cc16', '#8b5cf6',
];

const STAGE_CONFIG = {
  // ── Planification ──────────────────────────────────────────────────────────
  'Planifié':                          { color: '#185FA5', bg: 'rgba(24,95,165,0.12)' },
  'Planifiée':                         { color: '#185FA5', bg: 'rgba(24,95,165,0.12)' },
  'OT Planifiée':                      { color: '#185FA5', bg: 'rgba(24,95,165,0.12)' },
  'A_faire':                           { color: '#B8860B', bg: 'rgba(184,134,11,0.12)' },
  'A faire':                           { color: '#B8860B', bg: 'rgba(184,134,11,0.12)' },
  'A planifier':                       { color: '#B8860B', bg: 'rgba(184,134,11,0.12)' },
  // Un créneau réservé que le client n'a pas encore accepté : ni une
  // planification ferme, ni une commande en attente.
  'Proposition RDV envoyée':           { color: '#92400E', bg: 'rgba(217,164,6,0.18)' },
  // ── En cours ───────────────────────────────────────────────────────────────
  'En cours':                          { color: '#f97316', bg: 'rgba(249,115,22,0.12)' },
  'IAT':                               { color: '#C0392B', bg: 'rgba(192,57,43,0.12)' },
  // ── Résultats positifs ─────────────────────────────────────────────────────
  'CLEARED':                           { color: '#1E6B0A', bg: 'rgba(30,107,10,0.12)' },
  'Cleared':                           { color: '#1E6B0A', bg: 'rgba(30,107,10,0.12)' },
  'CLEARED suite reprise':             { color: '#1E8449', bg: 'rgba(30,132,73,0.12)' },
  // ── Résultats négatifs ─────────────────────────────────────────────────────
  'FAILED':                            { color: '#C0392B', bg: 'rgba(192,57,43,0.12)' },
  'Failed':                            { color: '#C0392B', bg: 'rgba(192,57,43,0.12)' },
  'RDVNH':                             { color: '#7B241C', bg: 'rgba(123,36,28,0.12)' },
  // ── CRI ────────────────────────────────────────────────────────────────────
  'CRi non cloturé':                   { color: '#7B241C', bg: 'rgba(123,36,28,0.12)' },
  'CRI non cloturé':                   { color: '#7B241C', bg: 'rgba(123,36,28,0.12)' },
  'CR non conforme':                   { color: '#D35400', bg: 'rgba(211,84,0,0.12)' },
  // ── Validations ────────────────────────────────────────────────────────────
  'En attente validation Admin':       { color: '#A0197F', bg: 'rgba(160,25,127,0.12)' },
  'En attente validation DEVIS':       { color: '#B8860B', bg: 'rgba(184,134,11,0.12)' },
  'En attente validation DOSTEC/ARC':  { color: '#6C3483', bg: 'rgba(108,52,131,0.12)' },
  // ── Divers ─────────────────────────────────────────────────────────────────
  'Commande gelée':                    { color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
  'Annulé':                            { color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
  'Closed Won':                        { color: '#1E6B0A', bg: 'rgba(30,107,10,0.12)' },
  'Closed Lost':                       { color: '#C0392B', bg: 'rgba(192,57,43,0.12)' },
  'Qualification':                     { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
};

function stageCfg(stage) {
  return STAGE_CONFIG[stage] || { color: '#8b92a5', bg: 'rgba(139,146,165,0.15)' };
}

function StageBadge({ stage }) {
  if (!stage) return <span style={{ fontSize: 10, color: 'var(--text3)' }}>—</span>;
  const cfg = stageCfg(stage);
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
      color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap',
    }}>
      {stage}
    </span>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────
export default function Planning() {
  const { settings } = useSettings();
  const settingsApplied = useRef(false);
  const [view, setView]                     = useState('week');
  const [refDate, setRefDate]               = useState(new Date());
  const [interventions, setInterventions]   = useState([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [techniciens, setTechniciens]       = useState([]);
  const [absences, setAbsences]             = useState([]);
  const [feries, setFeries]                 = useState({});
  const [selectedTechs, setSelectedTechs]   = useState(new Set());
  const [selectedStages, setSelectedStages] = useState(new Set());
  const [modal, setModal]                   = useState(null);
  const [search, setSearch]                 = useState('');
  const [secteur, setSecteur]               = useState('tous');
  const [datePickerVal, setDatePickerVal]   = useState('');

  // Apply settings defaults once loaded
  useEffect(() => {
    if (settings && !settingsApplied.current) {
      settingsApplied.current = true;
      if (settings.pages?.planning?.secteur_defaut) setSecteur(settings.pages.planning.secteur_defaut);
      if (settings.pages?.planning?.vue_defaut) setView(settings.pages.planning.vue_defaut);
    }
  }, [settings]);

  // ── Compute date range ──────────────────────────────────────────────────
  const { dateDebut, dateFin, days } = useMemo(() => {
    if (view === 'week') {
      const start = startOfWeek(refDate, { weekStartsOn: 1 });
      const end   = endOfWeek(refDate,   { weekStartsOn: 1 });
      return {
        dateDebut: format(start, 'yyyy-MM-dd'),
        dateFin:   format(end,   'yyyy-MM-dd'),
        days: eachDayOfInterval({ start, end }),
      };
    } else if (view === '3days') {
      const start = refDate;
      const end   = addDays(refDate, 2);
      return {
        dateDebut: format(start, 'yyyy-MM-dd'),
        dateFin:   format(end,   'yyyy-MM-dd'),
        days: eachDayOfInterval({ start, end }),
      };
    } else {
      const mStart = startOfMonth(refDate);
      const mEnd   = endOfMonth(refDate);
      const gStart = startOfWeek(mStart, { weekStartsOn: 1 });
      const gEnd   = endOfWeek(mEnd,     { weekStartsOn: 1 });
      return {
        dateDebut: format(mStart, 'yyyy-MM-dd'),
        dateFin:   format(mEnd,   'yyyy-MM-dd'),
        days: eachDayOfInterval({ start: gStart, end: gEnd }),
      };
    }
  }, [view, refDate]);

  // ── Fetch interventions ─────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    setError(null);
    getAdminPlanning(dateDebut, dateFin)
      .then(data => setInterventions(data.interventions || []))
      .catch(() => setError('Erreur lors de la récupération des interventions'))
      .finally(() => setLoading(false));
  }, [dateDebut, dateFin]);

  // Absences des techniciens, pour les signaler dans les colonnes de jour :
  // une journée de congé explique un planning vide, et évite d'y chercher une
  // place.
  useEffect(() => {
    getAbsences({ depuis: new Date().toLocaleDateString('sv-SE') })
      .then(d => setAbsences(d.items || []))
      .catch(() => {});
  }, []);

  // Les jours fériés de la grille affichée. Les bornes sont celles des
  // colonnes, pas celles de la recherche : en vue mois, la grille déborde sur
  // les semaines voisines, et un 1er novembre qui tombe un samedi de bordure
  // doit se barrer lui aussi.
  useEffect(() => {
    if (!days.length) return;
    const de = format(days[0], 'yyyy-MM-dd');
    const a  = format(days[days.length - 1], 'yyyy-MM-dd');
    getJoursFeries(de, a)
      .then(d => setFeries(d.feries || {}))
      .catch(() => {});
  }, [days]);

  // ── Fetch registered techniciens ────────────────────────────────────────
  useEffect(() => {
    getTechniciens()
      .then(data => setTechniciens(data.techniciens || []))
      .catch(() => {});
  }, []);

  // ── Build tech → color map ──────────────────────────────────────────────
  const techColorMap = useMemo(() => {
    const map = {};
    techniciens.forEach((t, i) => {
      const key = t.zoho_nom || `${t.prenom} ${t.nom}`.trim();
      map[key] = TECH_COLORS[i % TECH_COLORS.length];
    });
    // techs present in data but not in DB
    [...new Set(interventions.map(i => i.technicien).filter(Boolean))].forEach(name => {
      if (!map[name]) map[name] = TECH_COLORS[Object.keys(map).length % TECH_COLORS.length];
    });
    return map;
  }, [techniciens, interventions]);

  const allTechNames = useMemo(() =>
    [...new Set(interventions.map(i => i.technicien).filter(Boolean))].sort(),
    [interventions]);

  const allStages = useMemo(() =>
    [...new Set(interventions.map(i => i.stage || i.statut).filter(Boolean))].sort(),
    [interventions]);

  // ── Filtered data ───────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return interventions.filter(i => {
      if (selectedTechs.size > 0 && !selectedTechs.has(i.technicien)) return false;
      const sv = i.stage || i.statut;
      if (selectedStages.size > 0 && !selectedStages.has(sv)) return false;
      if (q && ![
        i.ref_commande, i.ref_work_order, i.client_final, i.technicien, i.client_operateur,
        i.rue, i.ville, i.code_postal, i.numero, i.n_via,
        i.commentaire_covage, i.commentaire_interne, i.commentaire_valea,
        i.commentaire_planif, i.commentaire_client, i.commentaire_vale,
        i.nom_contact, i.tel_fixe, i.tel_mobile, i.mail_contact,
      ].filter(Boolean).join(' ').toLowerCase().includes(q)) return false;
      if (secteur !== 'tous' && getSecteur(i.code_postal) !== secteur) return false;
      return true;
    });
  }, [interventions, selectedTechs, selectedStages, search, secteur]);

  const byDate = useMemo(() => {
    const map = {};
    filtered.forEach(i => {
      if (!map[i.date_intervention]) map[i.date_intervention] = [];
      map[i.date_intervention].push(i);
    });
    return map;
  }, [filtered]);

  // Les absences dépliées jour par jour. Le filtre de techniciens s'y applique
  // aussi : afficher le congé d'un technicien qu'on ne regarde pas encombre la
  // colonne sans rien apprendre.
  const absParJour = useMemo(() => {
    const map = {};
    absences.forEach(a => {
      if (selectedTechs.size > 0 && !selectedTechs.has(a.technicien)) return;
      const d = new Date(a.debut + 'T12:00:00');
      const f = new Date(a.fin + 'T12:00:00');
      let garde = 0;
      while (d <= f && garde++ < 400) {
        const k = d.toLocaleDateString('sv-SE');
        (map[k] = map[k] || []).push(a);
        d.setDate(d.getDate() + 1);
      }
    });
    return map;
  }, [absences, selectedTechs]);

  // ── Navigation ──────────────────────────────────────────────────────────
  const prev    = () => setRefDate(d => view === 'week' ? subWeeks(d, 1) : view === '3days' ? subDays(d, 3) : subMonths(d, 1));
  const next    = () => setRefDate(d => view === 'week' ? addWeeks(d, 1) : view === '3days' ? addDays(d, 3) : addMonths(d, 1));
  const goToday = () => setRefDate(new Date());

  const periodLabel = view === 'week'
    ? `${format(days[0], 'd MMM', { locale: fr })} – ${format(days[6], 'd MMM yyyy', { locale: fr })}`
    : view === '3days'
    ? `${format(days[0], 'd MMM', { locale: fr })} – ${format(days[2], 'd MMM yyyy', { locale: fr })}`
    : format(refDate, 'MMMM yyyy', { locale: fr });

  const toggleTech = name => setSelectedTechs(prev => {
    const s = new Set(prev); s.has(name) ? s.delete(name) : s.add(name); return s;
  });
  const toggleStage = stage => setSelectedStages(prev => {
    const s = new Set(prev); s.has(stage) ? s.delete(stage) : s.add(stage); return s;
  });

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      {/* TOPBAR */}
      <div className="topbar">
        <div>
          <div className="topbar-title">Planning</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, textTransform: 'capitalize' }}>{periodLabel}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* Recherche */}
          <input
            className="form-input"
            style={{ width: 190, fontSize: 12 }}
            placeholder="🔍 Réf, client, technicien…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          {/* Date picker — aller à une date précise */}
          <input
            type="date"
            className="form-input"
            style={{ width: 148, fontSize: 12 }}
            value={datePickerVal}
            onChange={e => {
              const v = e.target.value;
              setDatePickerVal(v);
              if (v) {
                const [y, m, d] = v.split('-').map(Number);
                setRefDate(new Date(y, m - 1, d));
              }
            }}
            title="Aller à une date"
          />

          {/* Refresh */}
          <button className="btn btn-ghost btn-sm" onClick={() => {
            setLoading(true); setError(null);
            getAdminPlanning(dateDebut, dateFin)
              .then(d => setInterventions(d.interventions || []))
              .catch(() => setError('Erreur'))
              .finally(() => setLoading(false));
          }} title="Actualiser">↻</button>

          {/* View toggle */}
          <div style={{ display: 'flex', background: 'var(--bg3)', borderRadius: 8, padding: 3 }}>
            {[['3days','3 Jours'],['week','Semaine'],['month','Mois']].map(([v, lbl]) => (
              <button key={v} className="btn btn-sm" onClick={() => setView(v)} style={{
                background: view === v ? 'var(--bg2)' : 'none',
                color: view === v ? 'var(--text)' : 'var(--text3)',
                border: view === v ? '1px solid var(--border2)' : 'none',
                borderRadius: 6,
              }}>{lbl}</button>
            ))}
          </div>

          {/* Navigation */}
          <button className="btn btn-secondary btn-sm" onClick={prev}>‹</button>
          <button className="btn btn-secondary btn-sm" onClick={goToday}>Aujourd'hui</button>
          <button className="btn btn-secondary btn-sm" onClick={next}>›</button>
        </div>
      </div>

      {/* FILTERS */}
      <div style={{
        padding: '10px 28px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg2)', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
        minHeight: 48,
      }}>
        {/* Techniciens */}
        {allTechNames.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>Techs</span>
            {allTechNames.map(name => {
              const on = selectedTechs.size === 0 || selectedTechs.has(name);
              const c  = techColorMap[name] || '#8b92a5';
              return (
                <button key={name} onClick={() => toggleTech(name)} style={{
                  fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
                  background: on ? `${c}22` : 'var(--bg3)',
                  color: on ? c : 'var(--text3)',
                  border: `1px solid ${on ? c + '55' : 'transparent'}`,
                  transition: 'all .15s',
                }}>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: on ? c : 'var(--text3)', marginRight: 5, verticalAlign: 'middle' }} />
                  {name}
                </button>
              );
            })}
            {selectedTechs.size > 0 && (
              <button onClick={() => setSelectedTechs(new Set())}
                style={{ fontSize: 10, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                tout
              </button>
            )}
          </div>
        )}

        {allTechNames.length > 0 && allStages.length > 0 && (
          <div style={{ width: 1, height: 18, background: 'var(--border)', flexShrink: 0 }} />
        )}

        {/* Étapes */}
        {allStages.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>Étapes</span>
            {allStages.map(stage => {
              const on  = selectedStages.size === 0 || selectedStages.has(stage);
              const cfg = stageCfg(stage);
              return (
                <button key={stage} onClick={() => toggleStage(stage)} style={{
                  fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
                  background: on ? cfg.bg : 'var(--bg3)',
                  color: on ? cfg.color : 'var(--text3)',
                  border: `1px solid ${on ? cfg.color + '55' : 'transparent'}`,
                  transition: 'all .15s',
                }}>
                  {stage}
                </button>
              );
            })}
            {selectedStages.size > 0 && (
              <button onClick={() => setSelectedStages(new Set())}
                style={{ fontSize: 10, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                tout
              </button>
            )}
          </div>
        )}

        {/* Secteur */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>Secteur</span>
          {[['tous','Tous'],['A','Secteur A'],['B','Secteur B']].map(([v, lbl]) => (
            <button key={v} onClick={() => setSecteur(v)} style={{
              fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
              background: secteur === v ? 'rgba(24,95,165,0.15)' : 'var(--bg3)',
              color: secteur === v ? '#185FA5' : 'var(--text3)',
              border: `1px solid ${secteur === v ? '#185FA5' : 'transparent'}`,
              transition: 'all .15s',
            }}>{lbl}</button>
          ))}
        </div>

        {/* Count */}
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>
          {loading
            ? <span style={{ color: 'var(--text3)' }}>Chargement…</span>
            : <span>{filtered.length} intervention{filtered.length > 1 ? 's' : ''}</span>
          }
        </div>
      </div>

      {/* CALENDAR AREA */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px' }}>
        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            color: '#ef4444', padding: '12px 16px', borderRadius: 10, marginBottom: 16, fontSize: 13,
          }}>{error}</div>
        )}

        {loading && interventions.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text3)', padding: '60px 0', fontSize: 13 }}>
            Chargement des interventions…
          </div>
        ) : view === 'week' ? (
          <WeekView days={days} byDate={byDate} techColorMap={techColorMap} onSelect={setModal} absParJour={absParJour} feries={feries} />
        ) : view === '3days' ? (
          <ThreeDayView days={days} byDate={byDate} techColorMap={techColorMap} onSelect={setModal} feries={feries} />
        ) : (
          <MonthView days={days} refDate={refDate} byDate={byDate} techColorMap={techColorMap} onSelect={setModal} feries={feries} />
        )}
      </div>

      {/* MODAL */}
      {modal && (
        <InterventionModal intervention={modal} techColorMap={techColorMap} onClose={() => setModal(null)} />
      )}
    </>
  );
}

// ─── Week view ───────────────────────────────────────────────────────────────
/**
 * Un jour férié, dit sur la colonne.
 *
 * Rien ne le signalait : une intervention posée un 1er mai se lisait comme
 * n'importe quelle autre, et personne ne s'en apercevait avant la veille. Le
 * fond hachuré barre la journée, le nom dit laquelle — « férié » tout seul
 * laisse vérifier au calendrier.
 *
 * Le rouge sert déjà à marquer aujourd'hui, mais autrement : un trait de
 * bordure et une pastille sur le chiffre. Ici c'est toute la case qui est
 * hachurée, et les deux se lisent ensemble quand un férié tombe aujourd'hui.
 */
const HACHURE_FERIE = 'repeating-linear-gradient(45deg, rgba(212,43,43,.13),'
  + ' rgba(212,43,43,.13) 6px, transparent 6px, transparent 12px)';

function BandeFerie({ nom }) {
  if (!nom) return null;
  return (
    <div title={nom + ' — jour férié'} style={{
      padding: '3px 6px', margin: '5px 5px 0', borderRadius: 5,
      fontSize: 9.5, lineHeight: 1.4, fontWeight: 700,
      color: 'var(--accent2)', background: 'rgba(212,43,43,.12)',
      border: '1px solid rgba(212,43,43,.34)',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    }}>
      ⛔ Férié · {nom}
    </div>
  );
}

function BandeAbsences({ liste }) {
  if (!liste || !liste.length) return null;
  return (
    <div style={{
      padding: '3px 6px', margin: '5px 5px 0',
      borderRadius: 5, fontSize: 9, lineHeight: 1.4,
      color: 'var(--text3)',
      background: 'repeating-linear-gradient(45deg, var(--bg3), var(--bg3) 5px, transparent 5px, transparent 10px)',
      border: '1px dashed var(--border)',
    }}>
      {liste.map((a, i) => (
        <div key={i} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          ⛔ {a.technicien.replace('VALEA-', '')} — {a.motif}
          {a.portee !== 'journee' ? ' (' + (a.portee === 'matin' ? 'matin' : 'après-midi') + ')' : ''}
        </div>
      ))}
    </div>
  );
}

function WeekView({ days, byDate, techColorMap, onSelect, absParJour = {}, feries = {} }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, minWidth: 700 }}>
      {days.map(day => {
        const key    = format(day, 'yyyy-MM-dd');
        const items  = (byDate[key] || []).slice().sort((a, b) => (a.heure_intervention || '').localeCompare(b.heure_intervention || ''));
        const today  = isToday(day);
        const ferie  = feries[key] || null;
        return (
          <div key={key} style={{
            background: 'var(--bg2)',
            backgroundImage: ferie ? HACHURE_FERIE : undefined,
            border: `1px solid ${ferie || today ? 'rgba(212,43,43,0.6)' : 'var(--border)'}`,
            borderRadius: 12, overflow: 'hidden', minHeight: 200,
          }}>
            {/* Day header */}
            <div style={{
              padding: '7px 10px',
              background: ferie ? 'rgba(212,43,43,0.16)' : today ? 'rgba(212,43,43,0.1)' : 'var(--bg3)',
              borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: ferie || today ? 'var(--accent2)' : 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                {format(day, 'EEE', { locale: fr })}
              </span>
              <span style={{
                width: 24, height: 24, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: today ? 'var(--accent)' : 'none',
                fontSize: 12, fontWeight: 700,
                color: today ? '#fff' : 'var(--text2)',
              }}>
                {format(day, 'd')}
              </span>
            </div>
            <BandeFerie nom={ferie} />
            <BandeAbsences liste={absParJour[key]} />
            {/* Events */}
            <div style={{ padding: 5, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {items.length === 0
                ? <div style={{ color: 'var(--text3)', fontSize: 11, textAlign: 'center', padding: '18px 0' }}>—</div>
                : items.map(item => (
                    <EventCard key={item.id} item={item} techColorMap={techColorMap} onSelect={onSelect} />
                  ))
              }
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Month view ──────────────────────────────────────────────────────────────
function MonthView({ days, refDate, byDate, techColorMap, onSelect, feries = {} }) {
  const currentMonth = format(refDate, 'MM');
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  // Hauteur dynamique : plus de semaines = cellules moins hautes, moins = plus hautes
  const cellMinH = weeks.length <= 5 ? 140 : 120;

  return (
    <div>
      {/* Header jours */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
        {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'var(--text3)', padding: '4px 0', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            {d}
          </div>
        ))}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
          {week.map(day => {
            const key        = format(day, 'yyyy-MM-dd');
            const items      = (byDate[key] || []).slice().sort((a,b) => (a.heure_intervention||'').localeCompare(b.heure_intervention||''));
            const today      = isToday(day);
            const ferie      = feries[key] || null;
            const otherMonth = format(day, 'MM') !== currentMonth;
            return (
              <div key={key} style={{
                background: otherMonth ? 'rgba(30,37,53,0.4)' : 'var(--bg2)',
                backgroundImage: ferie ? HACHURE_FERIE : undefined,
                border: `1px solid ${ferie || today ? 'rgba(212,43,43,0.6)' : 'var(--border)'}`,
                borderRadius: 8, padding: '6px 6px', minHeight: cellMinH,
                opacity: otherMonth ? 0.55 : 1,
                display: 'flex', flexDirection: 'column',
              }}>
                {/* En-tête date */}
                <div style={{
                  fontSize: 11, fontWeight: 700, marginBottom: 4,
                  color: ferie || today ? 'var(--accent2)' : otherMonth ? 'var(--text3)' : 'var(--text)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  flexShrink: 0,
                }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 20, height: 20, borderRadius: '50%',
                    background: today ? 'var(--accent)' : 'none',
                    color: today ? '#fff' : 'inherit',
                    fontSize: 11,
                  }}>{format(day, 'd')}</span>
                  {items.length > 0 && (
                    <span style={{
                      fontSize: 9, fontWeight: 700,
                      background: 'var(--bg3)', color: 'var(--text3)',
                      borderRadius: 10, padding: '0 5px',
                    }}>{items.length}</span>
                  )}
                </div>
                {/* Le nom tient mal dans une case de mois : la bande se réduit
                    au mot « Férié », l'infobulle donne la fête. */}
                {ferie && (
                  <div title={ferie + ' — jour férié'} style={{
                    fontSize: 9, fontWeight: 700, color: 'var(--accent2)',
                    marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden',
                    textOverflow: 'ellipsis', flexShrink: 0,
                  }}>⛔ {ferie}</div>
                )}
                {/* Liste interventions scrollable */}
                <div style={{
                  flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2,
                  scrollbarWidth: 'thin',
                }}>
                  {items.length === 0
                    ? null
                    : items.map(item => (
                        <EventCardMini key={item.id} item={item} techColorMap={techColorMap} onSelect={onSelect} showTech />
                      ))
                  }
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── 3-day view ───────────────────────────────────────────────────────────────
function ThreeDayView({ days, byDate, techColorMap, onSelect, feries = {} }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, minWidth: 600 }}>
      {days.map(day => {
        const key   = format(day, 'yyyy-MM-dd');
        const items = (byDate[key] || []).slice().sort((a, b) => (a.heure_intervention || '').localeCompare(b.heure_intervention || ''));
        const today = isToday(day);
        const ferie = feries[key] || null;
        return (
          <div key={key} style={{
            background: 'var(--bg2)',
            backgroundImage: ferie ? HACHURE_FERIE : undefined,
            border: `1px solid ${ferie || today ? 'rgba(212,43,43,0.6)' : 'var(--border)'}`,
            borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column',
            minHeight: 400,
          }}>
            {/* En-tête jour */}
            <div style={{
              padding: '10px 14px',
              background: ferie ? 'rgba(212,43,43,0.16)' : today ? 'rgba(212,43,43,0.1)' : 'var(--bg3)',
              borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: ferie || today ? 'var(--accent2)' : 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.07em' }}>
                  {format(day, 'EEEE', { locale: fr })}
                  {ferie && <span style={{ textTransform: 'none', letterSpacing: 0 }}> · ⛔ {ferie}</span>}
                </div>
                <div style={{
                  fontSize: 22, fontWeight: 800,
                  color: ferie || today ? 'var(--accent2)' : 'var(--text)',
                  lineHeight: 1.1,
                }}>
                  {format(day, 'd')}
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text3)', marginLeft: 5 }}>
                    {format(day, 'MMM yyyy', { locale: fr })}
                  </span>
                </div>
              </div>
              <span style={{
                fontSize: 12, fontWeight: 700,
                background: items.length > 0 ? 'var(--accent)' : 'var(--bg2)',
                color: items.length > 0 ? '#fff' : 'var(--text3)',
                borderRadius: 20, padding: '2px 10px', minWidth: 28, textAlign: 'center',
              }}>{items.length}</span>
            </div>
            {/* Interventions */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {items.length === 0
                ? <div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: '30px 0' }}>Aucune intervention</div>
                : items.map(item => (
                    <EventCard key={item.id} item={item} techColorMap={techColorMap} onSelect={onSelect} />
                  ))
              }
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Event cards ─────────────────────────────────────────────────────────────
/**
 * L'heure E-RDV choisie par le client, et son écart avec la nôtre.
 *
 * Date_de_E_RDV porte le choix du client, Heure_de_l_intervention notre
 * planification, et elles divergent. Sur les trente prochains jours, quatorze
 * interventions tombent le bon jour à la mauvaise heure — se présenter à 11 h
 * quand le client attend à 9 h, c'est un rendez-vous manqué. Le planning doit
 * donc montrer la sienne.
 */
function erdvDe(item) {
  const brut = item && item.date_erdv;
  if (!brut) return null;
  const m = String(brut).match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2})/);
  if (!m) return null;
  const jour = `${m[3]}-${m[2]}-${m[1]}`;
  const heure = `${m[4]}:${m[5]}`;
  const notre = String(item.heure_intervention || '').match(/(\d{2}):(\d{2})/);
  const notreHeure = notre ? `${notre[1]}:${notre[2]}` : null;
  const memeJour = jour === String(item.date_intervention || '').slice(0, 10);
  return {
    jour, heure, memeJour,
    ecartHeure: memeJour && notreHeure && notreHeure !== heure,
    notreHeure,
  };
}

function EventCard({ item, techColorMap, onSelect }) {
  const color = techColorMap[item.technicien] || '#8b92a5';
  const stage = item.stage || item.statut;
  const cfg   = stage ? stageCfg(stage) : null;
  return (
    <div onClick={() => onSelect(item)}
      // Un créneau réservé que le client n'a pas encore accepté se lisait
      // comme une intervention ferme : même fond, même trait. Le pointillé et
      // la transparence disent qu'il peut encore disparaître.
      style={{ padding: '6px 8px', borderRadius: 7, cursor: 'pointer',
               background: item.fantome ? 'transparent' : `${color}18`,
               borderLeft: `3px ${item.fantome ? 'dashed' : 'solid'} ${color}`,
               border: item.fantome ? `1px dashed ${color}88` : undefined,
               borderLeftWidth: 3, borderLeftStyle: item.fantome ? 'dashed' : 'solid',
               opacity: item.fantome ? 0.85 : 1,
               transition: 'background .12s' }}
      onMouseEnter={e => e.currentTarget.style.background = `${color}28`}
      onMouseLeave={e => e.currentTarget.style.background = item.fantome ? 'transparent' : `${color}18`}
    >
      {/* Ligne 1 : heure + étape */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
        {item.heure_intervention && (
          <span style={{ fontSize: 9, color: color, fontWeight: 700 }}>{item.heure_intervention}</span>
        )}
        {(() => { const e = erdvDe(item); if (!e) return null;
          return e.ecartHeure ? (
            <span title={`Le client a choisi ${e.heure}, nous avons planifié ${e.notreHeure}`}
              style={{ fontSize: 9, fontWeight: 800, padding: '1px 5px', borderRadius: 8, color: '#fff', background: '#dc2626', whiteSpace: 'nowrap' }}>
              ⚠ E-RDV {e.heure}
            </span>
          ) : (
            <span title={`Créneau choisi par le client${e.memeJour ? '' : ' le ' + e.jour}`}
              style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8, color: '#15803d', background: '#dcfce7', whiteSpace: 'nowrap' }}>
              🔒 E-RDV{e.memeJour ? '' : ' ' + e.jour.slice(8) + '/' + e.jour.slice(5, 7)}
            </span>
          );
        })()}
        {cfg && (
          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 10, color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap' }}>
            {item.fantome ? '⏳ ' : ''}{stage}
          </span>
        )}
        {item.fantome && item.expire_le && (
          <span title={'Le client doit répondre avant le '
            + new Date(item.expire_le).toLocaleDateString('fr-FR',
                { weekday: 'long', day: 'numeric', month: 'long' })}
            style={{ fontSize: 9, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
            réponse avant le {new Date(item.expire_le).toLocaleDateString('fr-FR',
              { day: '2-digit', month: '2-digit' })}
          </span>
        )}
      </div>
      {/* CX / PR badges */}
      {(item.complexite || item.priorite) && (
        <div style={{ display: 'flex', gap: 3, marginBottom: 2, flexWrap: 'wrap' }}>
          {item.complexite && (
            <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
              color: item.complexite==='CX1'?'#15803d':item.complexite==='CX2'?'#b45309':'#b91c1c',
              background: item.complexite==='CX1'?'#dcfce7':item.complexite==='CX2'?'#fef3c7':'#fee2e2',
              border: `1px solid ${item.complexite==='CX1'?'#86efac':item.complexite==='CX2'?'#fde68a':'#fecaca'}` }}>
              {item.complexite}
            </span>
          )}
          {item.priorite && (
            <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
              color: item.priorite==='PR1'?'#15803d':item.priorite==='PR2'?'#b45309':'#b91c1c',
              background: item.priorite==='PR1'?'#dcfce7':item.priorite==='PR2'?'#fef3c7':'#fee2e2',
              border: `1px solid ${item.priorite==='PR1'?'#86efac':item.priorite==='PR2'?'#fde68a':'#fecaca'}` }}>
              {item.priorite}
            </span>
          )}
        </div>
      )}
      {/* Ligne 2 : typo + ref commande */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 1 }}>
        {item.typo_raccordement && (
          <span style={{ fontSize: 9, color: 'var(--text3)', background: 'var(--bg3)', padding: '1px 5px', borderRadius: 6, whiteSpace: 'nowrap', maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {item.typo_raccordement}
          </span>
        )}
        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {item.ref_commande || '—'}
        </span>
      </div>
      {/* Ligne 3 : client final */}
      <div style={{ fontSize: 10, color: 'var(--text2)', marginBottom: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {item.client_final || item.client_operateur || '—'}
      </div>
      {/* Ligne 3b : ville + CP */}
      {(item.ville || item.code_postal) && (
        <div style={{ fontSize: 9, color: 'var(--text3)', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {[item.code_postal, item.ville].filter(Boolean).join(' ')}
        </div>
      )}
      {/* Ligne 4 : tech + devis */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }} />
        <span style={{ fontSize: 9, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{item.technicien || '—'}</span>
        {item.devis_forfaitaire && (
          <span title="Devis forfaitaire" style={{ fontSize: 10, color: '#1D6F42', background: 'rgba(29,111,66,0.12)', borderRadius: 4, padding: '0 4px', fontWeight: 700, flexShrink: 0 }}>💰</span>
        )}
      </div>
    </div>
  );
}

function EventCardMini({ item, techColorMap, onSelect, showTech }) {
  const color = techColorMap[item.technicien] || '#8b92a5';
  const stage = item.stage || item.statut;
  const cfg   = stage ? stageCfg(stage) : null;
  return (
    <div onClick={() => onSelect(item)} style={{
      fontSize: 10, padding: '3px 6px', borderRadius: 5, cursor: 'pointer',
      background: `${color}18`, borderLeft: `2px solid ${color}`,
      transition: 'background .1s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = `${color}2e`}
      onMouseLeave={e => e.currentTarget.style.background = `${color}18`}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, overflow: 'hidden' }}>
        {item.heure_intervention && (
          <span style={{ fontSize: 9, color, fontWeight: 700, flexShrink: 0 }}>{item.heure_intervention}</span>
        )}
        {(() => { const e = erdvDe(item); if (!e) return null;
          return <span title={e.ecartHeure ? `Le client a choisi ${e.heure}` : 'Créneau choisi par le client'}
            style={{ fontSize: 9, flexShrink: 0, color: e.ecartHeure ? '#dc2626' : '#15803d', fontWeight: 800 }}>
            {e.ecartHeure ? '⚠' + e.heure.slice(0, 2) + 'h' : '🔒'}
          </span>;
        })()}
        <span style={{ fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {item.ref_commande || item.client_final || '—'}
        </span>
        {item.devis_forfaitaire && <span style={{ fontSize: 9, flexShrink: 0 }}>💰</span>}
      </div>
      {showTech && (item.technicien || item.typo_raccordement) && (
        <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 1, display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
          {item.technicien && (
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{item.technicien}</span>
          )}
          {item.typo_raccordement && (
            <span style={{ flexShrink: 0, fontSize: 8, background: 'var(--bg3)', borderRadius: 4, padding: '0 3px', whiteSpace: 'nowrap' }}>
              {item.typo_raccordement}
            </span>
          )}
        </div>
      )}
      {cfg && (
        <div style={{ marginTop: 2 }}>
          <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 4px', borderRadius: 8, color: cfg.color, background: cfg.bg }}>
            {stage}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Modal ───────────────────────────────────────────────────────────────────
function InterventionModal({ intervention: iv, techColorMap, onClose }) {
  const color   = techColorMap[iv.technicien] || '#8b92a5';
  const address = [iv.numero, iv.rue, iv.code_postal, iv.ville].filter(Boolean).join(' ');
  const stage   = iv.stage || iv.statut;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 16,
        padding: 24, width: '100%', maxWidth: 480, boxShadow: 'var(--shadow)',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, fontFamily: 'var(--mono)', marginBottom: 6 }}>
              {iv.ref_commande || iv.ref_work_order || '—'}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <StageBadge stage={stage} />
              {iv.typo_raccordement && (
                <span style={{ fontSize: 10, color: 'var(--text3)', padding: '2px 8px', borderRadius: 20, background: 'var(--bg3)' }}>
                  {iv.typo_raccordement}
                </span>
              )}
            </div>
          </div>
          <button className="btn btn-ghost" onClick={onClose} style={{ fontSize: 20, lineHeight: 1, padding: '2px 6px' }}>×</button>
        </div>

        {/* Info rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '70vh', overflowY: 'auto' }}>
          <InfoRow label="Technicien">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
              {iv.technicien || '—'}
            </span>
          </InfoRow>
          <InfoRow label="Date & heure">
            {iv.date_intervention || '—'}{iv.heure_intervention ? ' à ' + iv.heure_intervention : ''}
          </InfoRow>
          <InfoRow label="Client final">{iv.client_final || '—'}</InfoRow>
          <InfoRow label="Opérateur">{iv.client_operateur || '—'}</InfoRow>
          {address && <InfoRow label="Adresse">{address}</InfoRow>}
          {iv.ref_work_order && <InfoRow label="Réf. Work Order" mono>{iv.ref_work_order}</InfoRow>}
          {iv.n_via && <InfoRow label="N° VIA" mono>{iv.n_via}</InfoRow>}
          {iv.nacelle && (
            <InfoRow label="Nacelle">
              <span style={{ color: '#f59e0b', fontWeight: 700 }}>⚠ Nacelle à prévoir</span>
            </InfoRow>
          )}
          {iv.devis_forfaitaire && (
            <InfoRow label="Devis forfaitaire">
              <span style={{ color: '#1D6F42', fontWeight: 700 }}>
                Oui{iv.montant_devis_forfaitaire ? ` — ${iv.montant_devis_forfaitaire} €` : ''}
              </span>
            </InfoRow>
          )}
          {(iv.commentaire_covage || iv.commentaire_valea || iv.commentaire_interne) && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 2, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {iv.commentaire_covage && <InfoRow label="Commentaire COVAGE">{iv.commentaire_covage}</InfoRow>}
              {iv.commentaire_valea && <InfoRow label="Commentaire VALEA">{iv.commentaire_valea}</InfoRow>}
              {iv.commentaire_interne && <InfoRow label="Commentaire interne">{iv.commentaire_interne}</InfoRow>}
            </div>
          )}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
            <Link
              to={`/dossier/${iv.id}`}
              className="btn btn-primary"
              style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginBottom: 8 }}
            >
              Ouvrir la fiche du dossier
            </Link>
            <a
              href={`https://crm.zoho.eu/crm/tab/Potentials/${iv.id}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-secondary"
              style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}
            >
              🔗 Ouvrir dans Zoho OT
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, children, mono }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', width: 120, flexShrink: 0, paddingTop: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text)', fontFamily: mono ? 'var(--mono)' : 'inherit', flex: 1 }}>
        {children}
      </div>
    </div>
  );
}
